import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题区块样式
const TitleSection = styled('div', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 内容区块样式
const ContentSection = styled('section', {
  marginBottom: '3rem'
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  color: '$textPrimary'
});

// 区块描述样式
const SectionDescription = styled('p', {
  fontSize: '1.1rem',
  marginBottom: '2rem',
  color: '$textSecondary',
  lineHeight: 1.6,
  maxWidth: '800px'
});

// 图文区块样式
const ImageTextBlock = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '3rem',
  marginBottom: '3rem',
  
  '@media (max-width: 968px)': {
    flexDirection: 'column',
    gap: '2rem'
  },
  
  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse',
        
        '@media (max-width: 968px)': {
          flexDirection: 'column'
        }
      }
    }
  }
});

// 图片容器样式
const ImageContainer = styled('div', {
  flex: '1',
  height: '400px',
  borderRadius: '12px',
  overflow: 'hidden',
  
  '@media (max-width: 968px)': {
    width: '100%',
    height: '300px'
  }
});

// 图片样式
const Image = styled('div', {
  width: '100%',
  height: '100%',
  background: 'center/cover no-repeat'
});

// 文本内容样式
const TextContent = styled('div', {
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 文本内容标题样式
const TextTitle = styled('h3', {
  fontSize: '1.75rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 文本内容段落样式
const TextParagraph = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 列表容器样式
const ListContainer = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '1rem 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 列表项样式
const ListItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '1rem',
  fontSize: '1.1rem',
  lineHeight: 1.5
});

// 列表项图标样式
const ListItemIcon = styled('div', {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '$accentPrimary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: 'white',
  fontWeight: 'bold'
});

// 列表项文本样式
const ListItemText = styled('span', {
  flex: 1
});

// 算力计算器区块样式
const CalculatorSection = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2rem',
  marginBottom: '3rem'
});

// 计算器表单样式
const CalculatorForm = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '1.5rem',
  marginTop: '2rem'
});

// 表单组样式
const FormGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
});

// 标签样式
const Label = styled('label', {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: '$textSecondary'
});

// 输入框样式
const Input = styled('input', {
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  fontSize: '1rem',
  color: '$textPrimary',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 选择框样式
const Select = styled('select', {
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  fontSize: '1rem',
  color: '$textPrimary',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 计算器结果区块样式
const CalculatorResults = styled('div', {
  marginTop: '2rem',
  padding: '1.5rem',
  borderRadius: '8px',
  background: '$backgroundPrimary',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1.5rem'
});

// 结果项样式
const ResultItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  padding: '1rem',
  borderRadius: '8px',
  background: 'rgba(0,0,0,0.05)'
});

// 结果项标签样式
const ResultLabel = styled('span', {
  fontSize: '0.9rem',
  color: '$textSecondary'
});

// 结果项值样式
const ResultValue = styled('span', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$accentPrimary'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-2px)'
        }
      }
    },
    fullWidth: {
      true: {
        width: '100%'
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// FAQ区块样式
const FaqSection = styled('div', {
  marginTop: '2rem'
});

// FAQ项容器样式
const FaqItem = styled('div', {
  marginBottom: '1.5rem',
  padding: '1.5rem',
  borderRadius: '8px',
  background: '$backgroundSecondary',
  
  '&:last-child': {
    marginBottom: 0
  }
});

// FAQ问题样式
const FaqQuestion = styled('h4', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// FAQ答案样式
const FaqAnswer = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 行动召唤区块样式
const CtaSection = styled('div', {
  marginTop: '3rem',
  padding: '2.5rem',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  textAlign: 'center',
  color: 'white'
});

// 行动召唤标题样式
const CtaTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 行动召唤描述样式
const CtaDescription = styled('p', {
  fontSize: '1.1rem',
  marginBottom: '2rem',
  maxWidth: '800px',
  margin: '0 auto 2rem'
});

/**
 * NFT挖矿机制页面组件
 * 介绍NFT挖矿的工作原理和收益计算
 */
const NftFarmMining: React.FC = () => {
  const { t } = useTranslation();
  
  // FAQ数据
  const faqs = [
    {
      question: t('nftFarm.mining.faq.question1', '如何开始NFT挖矿？'),
      answer: t('nftFarm.mining.faq.answer1', '开始NFT挖矿非常简单，您只需要购买一个NFT矿工收藏品，然后将其质押到挖矿池中。一旦质押完成，您的NFT将自动开始为您产生挖矿收益，无需任何额外操作。')
    },
    {
      question: t('nftFarm.mining.faq.question2', 'NFT挖矿与传统挖矿有什么区别？'),
      answer: t('nftFarm.mining.faq.answer2', 'NFT挖矿与传统挖矿的主要区别在于，您不需要购买和维护物理矿机设备，也不需要支付高额的电费。NFT挖矿通过持有特定的NFT获得挖矿权益，系统自动分配相应的挖矿收益，更加环保和便捷。')
    },
    {
      question: t('nftFarm.mining.faq.question3', '我的NFT挖矿收益如何计算？'),
      answer: t('nftFarm.mining.faq.answer3', 'NFT挖矿收益基于您持有的NFT属性计算，主要考虑NFT的稀有度、算力属性和质押时长。不同系列和稀有度的NFT具有不同的基础收益率，您还可以通过升级NFT或组合多个NFT来提高收益。')
    },
    {
      question: t('nftFarm.mining.faq.question4', '我可以提高我的NFT挖矿收益吗？'),
      answer: t('nftFarm.mining.faq.answer4', '是的，您可以通过多种方式提高NFT挖矿收益：1) 升级您的NFT矿工属性；2) 购买和使用NFT挖矿工具；3) 将NFT部署在高收益的矿区土地上；4) 参与特殊挖矿活动获取额外奖励；5) 长期质押获得时间加成。')
    },
    {
      question: t('nftFarm.mining.faq.question5', 'NFT挖矿收益如何提取？'),
      answer: t('nftFarm.mining.faq.answer5', '您可以随时从挖矿池中提取累积的挖矿收益。收益将以相应的加密货币（BTC、ETH等）发送到您的钱包地址。提取操作通常在几分钟内完成，根据区块链网络状况可能略有不同。')
    }
  ];
  
  return (
    <PageContainer>
      <TitleSection>
        <Title>{t('nftFarm.mining.title', 'NFT挖矿机制')}</Title>
        <Subtitle>
          {t('nftFarm.mining.subtitle', '了解我们的NFT挖矿工作原理、收益计算和挖矿策略，让您的NFT挖矿之旅更加高效')}
        </Subtitle>
      </TitleSection>
      
      <ContentSection>
        <SectionTitle>{t('nftFarm.mining.howItWorks.title', '挖矿原理')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.mining.howItWorks.description', 'NFT挖矿是一种创新的数字资产挖矿方式，将NFT收藏品与虚拟挖矿过程结合，无需物理矿机设备即可参与加密货币挖矿。')}
        </SectionDescription>
        
        <ImageTextBlock>
          <ImageContainer>
            <Image style={{ background: 'url("/assets/nft-mining-process.jpg") center/cover no-repeat' }} />
          </ImageContainer>
          <TextContent>
            <TextTitle>{t('nftFarm.mining.virtualMining.title', '虚拟挖矿系统')}</TextTitle>
            <TextParagraph>
              {t('nftFarm.mining.virtualMining.description', '我们的虚拟挖矿系统通过智能合约将NFT与挖矿权益关联，每个NFT代表特定的虚拟挖矿算力和属性。当您持有NFT时，系统会自动根据NFT的属性为您分配相应的挖矿收益。')}
            </TextParagraph>
            
            <ListContainer>
              <ListItem>
                <ListItemIcon>1</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.step1', '购买或获取NFT矿工收藏品')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>2</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.step2', '将NFT矿工质押到挖矿池中')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>3</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.step3', '系统根据NFT属性自动计算挖矿收益')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>4</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.step4', '定期获得加密货币奖励')}</ListItemText>
              </ListItem>
            </ListContainer>
          </TextContent>
        </ImageTextBlock>
        
        <ImageTextBlock reverse>
          <ImageContainer>
            <Image style={{ background: 'url("/assets/nft-mining-rewards.jpg") center/cover no-repeat' }} />
          </ImageContainer>
          <TextContent>
            <TextTitle>{t('nftFarm.mining.rewardsSystem.title', '收益分配机制')}</TextTitle>
            <TextParagraph>
              {t('nftFarm.mining.rewardsSystem.description', '我们的收益分配机制采用透明公平的算法，根据多种因素计算每个NFT的挖矿收益，确保所有参与者都能获得合理的回报。')}
            </TextParagraph>
            
            <ListContainer>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.rewardsFactor1', 'NFT稀有度：越稀有的NFT具有越高的基础收益率')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.rewardsFactor2', '算力属性：每个NFT都有特定的算力值，决定基础挖矿效率')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.rewardsFactor3', '质押时长：长期质押可获得额外的时间加成')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.rewardsFactor4', '属性加成：通过工具、土地等NFT获得额外收益加成')}</ListItemText>
              </ListItem>
            </ListContainer>
          </TextContent>
        </ImageTextBlock>
      </ContentSection>
      
      <CalculatorSection>
        <SectionTitle>{t('nftFarm.mining.calculator.title', '挖矿收益计算器')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.mining.calculator.description', '使用我们的收益计算器，估算您的NFT挖矿潜在收益。选择NFT类型、数量和质押时间，查看预期收益。')}
        </SectionDescription>
        
        <CalculatorForm>
          <FormGroup>
            <Label htmlFor="nft-type">{t('nftFarm.mining.calculator.nftType', 'NFT类型')}</Label>
            <Select id="nft-type" aria-label={t('nftFarm.mining.calculator.nftType', 'NFT类型')}>
              <option value="common">{t('nftFarm.rarities.common', '普通')} (0.001 BTC/日)</option>
              <option value="uncommon">{t('nftFarm.rarities.uncommon', '不普通')} (0.002 BTC/日)</option>
              <option value="rare">{t('nftFarm.rarities.rare', '稀有')} (0.004 BTC/日)</option>
              <option value="epic">{t('nftFarm.rarities.epic', '史诗')} (0.008 BTC/日)</option>
              <option value="legendary">{t('nftFarm.rarities.legendary', '传说')} (0.015 BTC/日)</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="nft-quantity">{t('nftFarm.mining.calculator.quantity', 'NFT数量')}</Label>
            <Input 
              type="number" 
              id="nft-quantity" 
              min="1" 
              max="100" 
              defaultValue="1" 
              aria-label={t('nftFarm.mining.calculator.quantity', 'NFT数量')}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="stake-duration">{t('nftFarm.mining.calculator.stakeDuration', '质押时间（天）')}</Label>
            <Input 
              type="number" 
              id="stake-duration" 
              min="1" 
              max="365" 
              defaultValue="30" 
              aria-label={t('nftFarm.mining.calculator.stakeDuration', '质押时间（天）')}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="bonus-multiplier">{t('nftFarm.mining.calculator.bonusMultiplier', '加成倍率')}</Label>
            <Select id="bonus-multiplier" aria-label={t('nftFarm.mining.calculator.bonusMultiplier', '加成倍率')}>
              <option value="1">x1 ({t('nftFarm.mining.calculator.noBonus', '无加成')})</option>
              <option value="1.1">x1.1 (+10%)</option>
              <option value="1.25">x1.25 (+25%)</option>
              <option value="1.5">x1.5 (+50%)</option>
              <option value="2">x2 (+100%)</option>
            </Select>
          </FormGroup>
        </CalculatorForm>
        
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Button variant="primary">
            {t('nftFarm.mining.calculator.calculate', '计算收益')}
          </Button>
        </div>
        
        <CalculatorResults>
          <ResultItem>
            <ResultLabel>{t('nftFarm.mining.calculator.dailyReward', '每日收益')}</ResultLabel>
            <ResultValue>0.002 BTC</ResultValue>
          </ResultItem>
          <ResultItem>
            <ResultLabel>{t('nftFarm.mining.calculator.weeklyReward', '每周收益')}</ResultLabel>
            <ResultValue>0.014 BTC</ResultValue>
          </ResultItem>
          <ResultItem>
            <ResultLabel>{t('nftFarm.mining.calculator.monthlyReward', '每月收益')}</ResultLabel>
            <ResultValue>0.06 BTC</ResultValue>
          </ResultItem>
          <ResultItem>
            <ResultLabel>{t('nftFarm.mining.calculator.totalReward', '总收益')}</ResultLabel>
            <ResultValue>0.06 BTC</ResultValue>
          </ResultItem>
        </CalculatorResults>
      </CalculatorSection>
      
      <ContentSection>
        <SectionTitle>{t('nftFarm.mining.strategies.title', '挖矿策略')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.mining.strategies.description', '探索最佳的NFT挖矿策略，最大化您的挖矿收益和投资回报。')}
        </SectionDescription>
        
        <ImageTextBlock>
          <ImageContainer>
            <Image style={{ background: 'url("/assets/nft-mining-strategies.jpg") center/cover no-repeat' }} />
          </ImageContainer>
          <TextContent>
            <TextTitle>{t('nftFarm.mining.optimizingReturns.title', '收益优化策略')}</TextTitle>
            <TextParagraph>
              {t('nftFarm.mining.optimizingReturns.description', '通过合理组合不同类型的NFT和优化质押策略，可以显著提高挖矿收益。以下是一些推荐的优化策略：')}
            </TextParagraph>
            
            <ListContainer>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.strategy1', '多元化投资：持有不同稀有度和类型的NFT矿工')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.strategy2', '使用工具NFT：为高价值矿工NFT装备合适的工具NFT')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.strategy3', '土地部署：将矿工NFT部署在匹配的矿区土地上')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.strategy4', '长期质押：利用时间加成提高整体收益')}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>✓</ListItemIcon>
                <ListItemText>{t('nftFarm.mining.strategy5', '升级策略：集中资源升级高稀有度NFT')}</ListItemText>
              </ListItem>
            </ListContainer>
          </TextContent>
        </ImageTextBlock>
      </ContentSection>
      
      <FaqSection>
        <SectionTitle>{t('nftFarm.mining.faq.title', '常见问题')}</SectionTitle>
        
        {faqs.map((faq, index) => (
          <FaqItem key={index}>
            <FaqQuestion>{faq.question}</FaqQuestion>
            <FaqAnswer>{faq.answer}</FaqAnswer>
          </FaqItem>
        ))}
      </FaqSection>
      
      <CtaSection>
        <CtaTitle>{t('nftFarm.mining.cta.title', '开始您的NFT挖矿之旅')}</CtaTitle>
        <CtaDescription>
          {t('nftFarm.mining.cta.description', '立即购买您的第一个NFT矿工，体验创新的虚拟挖矿方式，轻松获取被动加密货币收益。')}
        </CtaDescription>
        <Link to="/products/nft-farm/collections">
          <Button variant="primary">
            {t('nftFarm.mining.cta.exploreNFTs', '探索NFT收藏品')}
          </Button>
        </Link>
      </CtaSection>
    </PageContainer>
  );
};

export default NftFarmMining; 